import React, { FC, ComponentType, ReactNode, useState, useCallback, useMemo, useRef, MouseEvent } from 'react';
import max from 'lodash/max';

import { SCP } from 'ia-react-core/~/interfaces/SCP';
import useTranslation from 'ia-react-core/~/hooks/useTranslation';
import useRect from 'ia-react-core/~/hooks/_useRect';
import useMutations from 'ia-react-core/~/hooks/_useMutations';
import { StyledMessage, StyledIcon, StyledMessageText, StyledTitle, StyledMessageContent } from './Message.styles';
import Link from '../../navigation/Link';
import Spacing from '../../grid/Spacing';

export interface MessageProps extends SCP<'aside'> {
  /** Custom error message. Alternative to using the `error`prop. */
  children?: ReactNode;
  /** The message title. */
  messageTitle?: string;
  /** The icon component */
  Icon?: ComponentType<any>;
  /** Color of the icon. */
  iconColor?: string;
  /** Force the error to always display vertically. */
  vertical?: boolean;
  /** Css text transform of the title. */
  titleTextTransform?: string;
  /** Maximum number of line to display. */
  maxTextLines?: number;
  /** Text of Read more button. */
  moreTextButton?: string;
}

const Message: FC<MessageProps> = ({
  children, messageTitle, Icon, iconColor, vertical, titleTextTransform, maxTextLines, moreTextButton, ...props
}) => {
  const { t } = useTranslation('Core.Message');
  const messageContentRef = useRef<HTMLSpanElement>();
  const messageInnerContentRef = useRef<HTMLSpanElement>();
  const [isExpanded, setIsExpanded] = useState(false);
  const [scrollHeight, setScrollHeight] = useState<number>(0);

  const messageContentRect = useRect(messageContentRef);

  useMutations(messageInnerContentRef, () => {
    setScrollHeight(messageContentRef?.current?.scrollHeight);
  }, { init: { childList: true, subtree: true } });

  const hasMoreText = useMemo(() => {
    const height = max([scrollHeight, messageContentRect.height]);
    const lineHeight = messageContentRef.current && window.getComputedStyle(messageContentRef.current).lineHeight;
    return height > maxTextLines * parseFloat(lineHeight);
  }, [maxTextLines, messageContentRect.height, scrollHeight]);

  const handleReadMoreClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsExpanded((latestIsExpanded) => !latestIsExpanded);
  }, []);

  return (
    <StyledMessage
      role="status"
      withIcon={Boolean(Icon)}
      iconColor={iconColor}
      $vertical={vertical}
      {...props}
    >
      {Icon && <StyledIcon as={Icon} $vertical={vertical} />}
      <StyledMessageText
        $vertical={vertical}
      >
        {messageTitle && <StyledTitle titleTextTransform={titleTextTransform}>{messageTitle}</StyledTitle>}
        <StyledMessageContent
          $maxTextLines={isExpanded ? null : maxTextLines}
          ref={messageContentRef}
        >
          <span ref={messageInnerContentRef}>
            {children}
          </span>
        </StyledMessageContent>
        {hasMoreText && (
          <Spacing p-t={12}>
            <Link
              as="button"
              onClick={handleReadMoreClick}
            >
              {moreTextButton || (isExpanded ? t('readLessButton') : t('readMoreButton'))}
            </Link>
          </Spacing>
        )}
      </StyledMessageText>
    </StyledMessage>
  );
};

export default Message;
